// Shared background-removal utilities used by BgRemoveTab and CaseReportTab.

export function bgrCanvasToBlob(canvas) {
  return new Promise((res, rej) =>
    canvas.toBlob(b => b ? res(b) : rej(new Error('canvas.toBlob returned null')), 'image/png'))
}

export async function bgrPsdToBlob(file, readPsd) {
  const buf = await file.arrayBuffer()
  const psd = readPsd(buf, { skipLayerImageData: true, skipThumbnail: true })
  if (psd.canvas) return bgrCanvasToBlob(psd.canvas)
  if (psd.imageData) {
    const c = document.createElement('canvas')
    c.width = psd.width; c.height = psd.height
    c.getContext('2d').putImageData(psd.imageData, 0, 0)
    return bgrCanvasToBlob(c)
  }
  throw new Error('PSD has no composite — re-save with "Maximize Compatibility" on')
}

export async function bgrDecodeFile(file, readPsd) {
  const ext = file.name.toLowerCase().match(/\.([^.]+)$/)?.[1]
  if (ext === 'psd') return bgrPsdToBlob(file, readPsd)
  return file
}

// Direct color-based background removal — no ML, instant, works on any solid background.
// BFS flood-fill from all 4 corners identifies the connected background region.
export async function bgrDirectRemove(sourceBlob, tolerance = 35) {
  const bitmap = await createImageBitmap(sourceBlob)
  const { width, height } = bitmap
  const c = document.createElement('canvas')
  c.width = width; c.height = height
  const ctx = c.getContext('2d')
  ctx.drawImage(bitmap, 0, 0)
  const imgData = ctx.getImageData(0, 0, width, height)
  const data = imgData.data
  const n = width * height

  const bg = bgrSampleBgColor(data, width, height)

  const bgCandidate = new Uint8Array(n)
  for (let i = 0; i < n; i++) {
    const dr = data[i*4] - bg.r, dg = data[i*4+1] - bg.g, db = data[i*4+2] - bg.b
    if (Math.sqrt(dr*dr + dg*dg + db*db) < tolerance) bgCandidate[i] = 1
  }

  const visited = new Uint8Array(n)
  const queue = new Int32Array(n)
  let head = 0, tail = 0

  const enq = (idx) => {
    if (!visited[idx] && bgCandidate[idx]) { visited[idx] = 1; queue[tail++] = idx }
  }
  for (let x = 0; x < width; x++) { enq(x); enq((height - 1) * width + x) }
  for (let y = 1; y < height - 1; y++) { enq(y * width); enq(y * width + width - 1) }

  while (head < tail) {
    const idx = queue[head++]
    const x = idx % width, y = (idx / width) | 0
    if (x > 0)          enq(idx - 1)
    if (x < width - 1)  enq(idx + 1)
    if (y > 0)          enq(idx - width)
    if (y < height - 1) enq(idx + width)
  }

  for (let i = 0; i < n; i++) {
    if (visited[i]) data[i * 4 + 3] = 0
  }

  const innerTol = Math.min(tolerance, 20)
  for (let i = 0; i < n; i++) {
    if (!visited[i] && data[i * 4 + 3] > 0) {
      const dr = data[i*4] - bg.r, dg = data[i*4+1] - bg.g, db = data[i*4+2] - bg.b
      if (Math.sqrt(dr*dr + dg*dg + db*db) < innerTol) data[i * 4 + 3] = 0
    }
  }

  bgrRemoveDarkFringe(data, width, height, 35)
  bgrSmoothAlphaEdges(data, width, height)

  for (let i = 0; i < n; i++) {
    if (visited[i] && data[i * 4 + 3] > 0) data[i * 4 + 3] = 0
  }
  for (let i = 0; i < n; i++) {
    if (!visited[i] && data[i * 4 + 3] > 0) {
      const dr = data[i*4] - bg.r, dg = data[i*4+1] - bg.g, db = data[i*4+2] - bg.b
      if (Math.sqrt(dr*dr + dg*dg + db*db) < innerTol) data[i * 4 + 3] = 0
    }
  }

  ctx.putImageData(imgData, 0, 0)
  return bgrCanvasToBlob(c)
}

function bgrSampleBgColor(data, width, height) {
  const patch = Math.min(20, Math.floor(Math.min(width, height) * 0.04))
  const rs = [], gs = [], bs = []
  for (let y = 0; y < patch; y++) {
    for (let x = 0; x < patch; x++) {
      const corners = [
        y * width + x,
        y * width + (width - 1 - x),
        (height - 1 - y) * width + x,
        (height - 1 - y) * width + (width - 1 - x),
      ]
      for (const i of corners) {
        rs.push(data[i * 4]); gs.push(data[i * 4 + 1]); bs.push(data[i * 4 + 2])
      }
    }
  }
  rs.sort((a, b) => a - b); gs.sort((a, b) => a - b); bs.sort((a, b) => a - b)
  const m = (rs.length / 2) | 0
  return { r: rs[m], g: gs[m], b: bs[m] }
}

function bgrSmoothAlphaEdges(data, width, height) {
  const n = width * height
  const src = new Uint8Array(n)
  for (let i = 0; i < n; i++) src[i] = data[i * 4 + 3]
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x
      const a = src[i]
      if ((x > 0 && src[i-1] !== a) || (x < width-1 && src[i+1] !== a) ||
          (y > 0 && src[i-width] !== a) || (y < height-1 && src[i+width] !== a)) {
        let sum = 0, cnt = 0
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const ny = y + dy, nx = x + dx
            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              sum += src[ny * width + nx]; cnt++
            }
          }
        }
        data[i * 4 + 3] = (sum / cnt + 0.5) | 0
      }
    }
  }
}

function bgrRemoveDarkFringe(data, width, height, lumThreshold) {
  const n = width * height
  const toRemove = new Uint8Array(n)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x
      if (data[i * 4 + 3] < 10) continue
      const adjTransp =
        (x > 0          && data[(i - 1)      * 4 + 3] < 10) ||
        (x < width - 1  && data[(i + 1)      * 4 + 3] < 10) ||
        (y > 0          && data[(i - width)   * 4 + 3] < 10) ||
        (y < height - 1 && data[(i + width)   * 4 + 3] < 10)
      if (!adjTransp) continue
      const lum = (data[i*4] * 77 + data[i*4+1] * 150 + data[i*4+2] * 29) >> 8
      if (lum < lumThreshold) toRemove[i] = 1
    }
  }
  for (let i = 0; i < n; i++) {
    if (toRemove[i]) data[i * 4 + 3] = 0
  }
}
